import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { PublicError } from '@microsoft/microsoft-graph-types';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { IdexService } from '../../idex.service';
import { Path } from '../../shared/path';
import { Toast } from '../../shared/toast/toast';
import { SharedLoaderService } from '../../sharedLoader.service';
import { Breadcrumb, BreadcrumbItem } from '../breadcrumb/breadcrumb.type';
import { OneDriveItem } from '../onedrive-list/onedrive-item.type';
import {
   ItemDMS,
   ItemList,
   OnedriveListComponent,
   RequestDeleteEvent,
   RequestDownloadEvent,
   RequestMoveEvent,
   RequestOpenEvent,
   RequestRenameEvent
} from '../onedrive-list/onedrive-list.component';
import {
   OneDriveMoveDialogComponent,
   OneDriveMoveDialogData,
   OneDriveMoveDialogResult
} from '../onedrive-move-dialog/onedrive-move-dialog.component';
import { OnedriveUploaderService } from '../onedrive-uploader.service';
import { CreateOneDriveComponent } from '../onedrive/create-onedrive.component';
import { UploadProgressComponent } from '../upload-progress/upload-progress.component';
import { ConfirmDeleteComponent } from '../confirm-delete/confirm-delete.component';

@Component({
   selector: 'app-onedrive-page',
   templateUrl: './onedrive-page.component.html',
   styleUrls: ['./onedrive-page.component.css']
})
export class OneDrivePageComponent implements OnInit, OnDestroy {
   @ViewChild('onedriveList') onedriveList: OnedriveListComponent<OneDriveItem>;
   @ViewChild('progress') progress: UploadProgressComponent;

   pageSize = 50;
   private PARAM_FOLDER_ID: string;
   protected access_token: string;
   private folderItem: OneDriveItem;
   public canDrag = true;
   /**
    * True if the page is currently fetching items from OneDrive. False otherwise.
    *
    * Used for preventing showing 'no items' message while fetching items.
    */
   protected isFetchingItems: boolean = false;
   /**
    * True if the page is currently fetching more items from OneDrive. False otherwise.
    *
    * Used for showing load-more loading animation.
    */
   protected isFetchingNextItems: boolean = false;

   pathFolder: string;

   /** A breadcrumb trail of OneDrive folders */
   breadcrumb: Breadcrumb;

   private readonly defaultBreadcrumbItems: BreadcrumbItem[] = [{ id: null, name: 'OneDrive', path: '' }];

   /** OneDriveItems that are currently being displayed/listed */
   displayedItems: ItemList<OneDriveItem>;

   /** OneDriveItem that the user clicks to select */
   selectedItems: OneDriveItem[] = [];

   /** Link to download next OneDriveItems after user scrolls to the end of the list */
   nextLink: {
      folderId: string;
      url: string;
   };

   private uploadCallback = {
      init: (files) => {
         this.progress.init(files);
      },
      initFolder: (folder, name: string) => {
         this.progress.initFolder(folder, name);
      },
      progress: (i: number, progress: number) => {
         this.progress.update(i, progress);
      },
      cancelUpload: (i: number, error: string) => {
         this.progress.cancelUpload(i, error);
      },
      complete: () => {
         const result = this.progress.complete();

         setTimeout(() => {
            this.progress.hide();
            this.listItems();
         }, this.progress.calcDelay());
      }
   };

   /**
    * A Subject to cancel any ongoing HTTP requests.
    */
   private cancel$ = new Subject<void>();

   constructor(
      private router: Router,
      private route: ActivatedRoute,
      protected service: IdexService,
      private translate: TranslateService,
      private dialog: MatDialog,
      private http: HttpClient,
      private toast: Toast,
      private loader: SharedLoaderService,
      public onedriveService: OnedriveUploaderService,
      private renderer: Renderer2
   ) {}

   ngOnInit() {
      this.breadcrumb = { id: undefined, items: this.defaultBreadcrumbItems };
      this.route.params.subscribe((param) => {
         this.service.getCheckTimeOut().subscribe(
            () => {
               this.folderItem = undefined;
               this.displayedItems = undefined;
               this.cancel$.next();
               this.isFetchingItems = true;
               this.isFetchingNextItems = false;

               this.PARAM_FOLDER_ID = param['id'];
               this.access_token = localStorage.getItem('access_token');
               if (!this.access_token) {
                  // Reload the page and let onedrive-auth-guard.service handle the login.
                  this.router.navigate(['/onedrivepage/' + this.PARAM_FOLDER_ID]);
               } else {
                  this.listItems();
               }
            },
            (error) => {
               // zDOX Timeout
               if (error.status === 401) {
                  this.translate.get('timeout').subscribe((s) => {
                     this.toast.error(s);
                  });
                  this.handlezDOXTimeout();
               }
            }
         );
      });
      this.pathFolder = this.folderItem ? this.folderItem.id : 'root';
   }

   ngOnDestroy() {
      this.loader.hide();
      this.loadItems$.unsubscribe();
   }

   //=============================================================================
   // METHODS TO DISPLAY FILES AND/OR FOLDERS
   //=============================================================================

   /**
    * Call the API, fetch, and re-render the current folder's items.
    */
   private refreshList(mode: 'hard' | 'soft' = 'hard') {
      switch (mode) {
         case 'hard':
            this.listItems();
            break;
         case 'soft':
            this.fetchItems();
            break;
         default:
            this.listItems();
      }
   }

   private loadItems$ = new BehaviorSubject<void>(undefined);
   private fetchItems$ = this.loadItems$.pipe(
      take(1),
      switchMap(async () => {
         if (!this.PARAM_FOLDER_ID) {
            this.folderItem = undefined;
            return this.listItemsInRoot(this.access_token);
         }
         this.pathFolder = this.PARAM_FOLDER_ID ? this.PARAM_FOLDER_ID : 'root';
         try {
            this.folderItem = await this.getItemFromId(this.access_token, this.PARAM_FOLDER_ID).toPromise();
         } catch (error) {
            console.error(error);
            this.router.navigate(['/onedrivepage/']);
            return EMPTY;
         }

         // Check if the item is really a folder
         if (this.folderItem?.folder) {
            return this.listItemsInFolder(this.access_token, this.folderItem);
         } else {
            this.router.navigate(['/onedrivepage/']);
            return EMPTY;
         }
      }),
      switchMap((observable) => {
         return observable;
      })
   );

   /**
    * Fetch and update DriveItem(s). Prevents the user from interacting with the page while fetching.
    * If folderId is not provided, list DriveItem within Root instead.
    */
   private listItems() {
      this.loader.show();
      this.cancel$.next();
      this.isFetchingItems = true;
      this.isFetchingNextItems = false;
      this.fetchItems();
   }

   /**
    * Fetch and update DriveItem(s) without showing the loader.
    * If folderId is not provided, list DriveItem within Root instead.
    */
   private fetchItems() {
      this.fetchItems$.subscribe(
         async (res) => {
            this.loader.show();
            // Display Breadcrumb Bar
            try {
               this.updateBreadcrumb();
            } catch (err) {
               if (err.status === 401) {
                  this.handlezDOXTimeout();
               }
            }

            // Display Items
            this.displayedItems = {
               id: this.folderItem?.id,
               items: this.OneDriveItemListToItemDMSList(res.value)
            };

            this.nextLink = {
               folderId: this.folderItem?.id,
               url: res['@odata.nextLink']
            };
            this.loader.hide();
         },
         (error) => {
            this.loader.hide();
            console.error(error);
            if (error.status === 401) {
               this.handlezDOXTimeout();
            }
         },
         () => {
            this.isFetchingItems = false;
            this.loader.hide();
         }
      );
      this.loadItems$.next();
   }

   private listItemsInRoot(access_token: string) {
      return this.service.onedriveList(access_token);
   }

   private listItemsInFolder(access_token: string, folder: OneDriveItem) {
      return this.service.onedriveListItems(access_token, folder.id, this.pageSize);
   }

   private getItemFromId(access_token: string, id: string) {
      return this.service.onedriveGetItemFromId(access_token, id);
   }

   private async updateBreadcrumb() {
      const tmpBreadcrumbId = this.folderItem?.id || '';
      const tmpBreadcrumbItems = await this.createBreadcrumb(this.folderItem, this.access_token);

      // If Breadcrumb ID mismatched. Skip updating breadcrumb. And then,
      // reload the page to make sure folderItem is fetched from the current PARAM_FOLDER_ID.
      if (
         tmpBreadcrumbId !== (this.folderItem?.id || '') ||
         tmpBreadcrumbId !== this.PARAM_FOLDER_ID ||
         (this.folderItem?.id || '') !== this.PARAM_FOLDER_ID
      ) {
         this.loader.hide();
         window.location.reload();
         return;
      }
      this.breadcrumb = {
         id: this.folderItem?.id,
         items: tmpBreadcrumbItems.length > 0 ? tmpBreadcrumbItems : this.defaultBreadcrumbItems
      };
   }

   protected OneDriveItemListToItemDMSList(oneDriveItems: OneDriveItem[]): ItemDMS<OneDriveItem>[] {
      return oneDriveItems.map((item) => this.OneDriveItemToItemDMS(item));
   }

   protected OneDriveItemToItemDMS(item: OneDriveItem): ItemDMS<OneDriveItem> {
      if (!item) {
         return;
      }

      return {
         id: item.id,
         name: item.name,
         mimeType: item.file ? item.file.mimeType : '',
         isFolder: Boolean(item.folder),
         item: item
      };
   }

   /**
    * Infinite scrolling to fetch next items when the user scrolls to the end of the list.
    * A.k.a. loadmore
    */
   @HostListener('window:scroll', ['$event'])
   onWindowScroll() {
      // In chrome and some browsers, 'scroll' is given to body tag.
      let pos = (document.documentElement.scrollTop || document.body.scrollTop) + document.documentElement.offsetHeight;
      let max = document.documentElement.scrollHeight;
      const url = this.nextLink?.url;
      if (
         pos >= 0.8 * max &&
         this.nextLink?.url &&
         !this.isFetchingNextItems &&
         this.folderItem?.id === this.nextLink?.folderId
      ) {
         // Fetching next items
         this.isFetchingNextItems = true;
         const headers = new HttpHeaders({
            Authorization: `Bearer ${this.access_token}`
         });
         this.http
            .get(url, { headers })
            .pipe(takeUntil(this.cancel$))
            .subscribe(
               (res: {
                  '@odata.context': string;
                  '@odata.count': number;
                  '@odata.nextlink'?: string;
                  value: OneDriveItem[];
               }) => {
                  if (url === this.nextLink?.url) {
                     this.displayedItems = {
                        id: this.folderItem?.id,
                        items: this.displayedItems.items.concat(this.OneDriveItemListToItemDMSList(res['value']))
                     };
                     this.nextLink = {
                        folderId: this.nextLink.folderId,
                        url: res['@odata.nextLink']
                     };
                  }
               },
               (error) => {
                  console.error(error);
               },
               () => {
                  this.isFetchingNextItems = false;
               }
            );
      }
   }

   /** Route to specified folder. Route to OneDrive root if 'folderId' is empty. */
   private openFolder(folderId?: string) {
      if (!folderId) {
         folderId = '';
         this.pathFolder = 'root';
      } else {
         this.pathFolder = folderId;
      }
      const url = Path.combine('/onedrivepage', folderId);
      this.router.navigate([url]);
   }

   //=============================================================================
   // BREADCRUMB-RELATED METHODS
   //=============================================================================

   /** Create Breadcrumb stack from Root to the input item */
   private async createBreadcrumb(item: OneDriveItem, onedrive_access_token: string): Promise<BreadcrumbItem[]> {
      if (!item) return [];

      // If the item is root folder, no need to create breadcrumb.
      if (item?.root) {
         return [];
      }

      const breadcrumbStack: BreadcrumbItem[] = [];

      // If the item is folder, include itself into the stack.
      if (item?.folder) {
         breadcrumbStack.push({
            id: item.id,
            name: item.name,
            path: null
         });
      }

      //| parentReference.path starts with "/drive/root:" follows by "/folder1/folder2/.../folderN"
      //| Example:
      //| "/drive/root:" = root directory.
      //| "/drive/root:/Apps/zDox" = root > Apps > zDox
      //|
      //| See https://learn.microsoft.com/en-us/graph/api/resources/itemreference
      //|_____________________________________________________________________________

      // Loop adding parent part to breadcrumb stack until the parent path is root.
      // If the last character of the path is ":", it's root directory.
      while (item?.parentReference?.path?.charAt(item?.parentReference?.path?.length - 1) !== ':') {
         item = await this.getItemFromId(onedrive_access_token, item.parentReference.id).toPromise();
         breadcrumbStack.push({
            name: item.name,
            path: Path.combine('/onedrivepage', item.id),
            id: item.id
         });
      }

      breadcrumbStack.push({
         id: null,
         name: await this.translate.get('main.onedrive').toPromise(),
         path: Path.combine('/onedrivepage')
      });

      return breadcrumbStack.reverse();
   }

   private addToBreadcrumb(currentBreadcrumb: Breadcrumb, breadcrumb: BreadcrumbItem): Breadcrumb {
      return {
         ...currentBreadcrumb,
         items: currentBreadcrumb.items.concat(breadcrumb)
      };
   }

   private trimBreadcrumb(currentBreadcrumb: Breadcrumb, clickedId: string): Breadcrumb {
      if (!currentBreadcrumb || !currentBreadcrumb.items || currentBreadcrumb.items.length === 0) {
         return currentBreadcrumb;
      }
      const index = currentBreadcrumb.items.findIndex((item) => item.id === clickedId);
      if (index === -1) {
         return currentBreadcrumb;
      }
      return {
         ...currentBreadcrumb,
         items: currentBreadcrumb.items.slice(0, index + 1)
      };
   }

   //=============================================================================
   // ON RECEIVING EMITTED EVENT FROM ONEDRIVE-LIST COMPONENT
   //=============================================================================

   protected onClickBreadcrumb(breadcrumb: BreadcrumbItem) {
      if (breadcrumb.path) {
         this.breadcrumb = this.trimBreadcrumb(this.breadcrumb, breadcrumb.id);
         this.openFolder(breadcrumb.id);
      }
   }

   protected onClickOpenItem(event: RequestOpenEvent<OneDriveItem>) {
      if (!event?.item?.item) return;

      const item = event.item.item;
      if (item.folder) {
         this.breadcrumb = this.addToBreadcrumb(this.breadcrumb, { id: item.id, name: item.name, path: null });
         this.openFolder(item.id);
      } else if (item.name.toLowerCase().endsWith('.zdox')) {
         this.service.getEFormByXml('onedrive', { access_token: this.access_token, fid: item.id, name: item.name}).subscribe((res) => {
            let url = location.origin + this.router.createUrlTree(['/eform/create/view/file']);
            if (res?.data) {
               let form = res.data;
               localStorage.setItem('form-type', 'view');
               localStorage.setItem('filename', item.name);
               localStorage.setItem('form-view', JSON.stringify(form));
               localStorage.setItem('isOpenFile', "true");
               window.open(url, '_blank');
            }
         });
      } else {
         // Open file with OneDrive's own viewer
         window.open(item.webUrl);
      }
   }

   protected moveItem(event: RequestMoveEvent<OneDriveItem>) {
      if (!event?.itemToMove?.item) return;
      const item = event.itemToMove.item;

      // Open a dialog to ask for a folder destination to move the item to.
      const moveDialogRef = this.dialog.open<
         OneDriveMoveDialogComponent,
         OneDriveMoveDialogData,
         OneDriveMoveDialogResult
      >(OneDriveMoveDialogComponent, {
         disableClose: true,
         data: {
            access_token: this.access_token,
            itemToMove: item
         }
      });
      moveDialogRef.afterClosed().subscribe((dialogResult) => {
         if (!dialogResult) return;

         // User clicks "cancel" after clicking "move" item and before the move request is completed.
         if (dialogResult.isMovingInProgress) {
            dialogResult.movingResult$?.subscribe((result) => {
               if (result === 'success') {
                  this.translate.get('alert.move_to').subscribe((s) => {
                     this.toast.success(item.name + s + dialogResult.name, 5000);
                  });
                  this.refreshList('soft');
               }
            });
         }
         // Move is success
         else {
            // Re-list items in the current folder (or root folder)
            this.refreshList('soft');
         }
      });
   }

   protected renameItem(event: RequestRenameEvent<OneDriveItem>) {
      if (!event?.currentItem?.item) return;

      this.loader.show();
      const newName = event.newName.trim();
      this.updateOneDriveItem(this.access_token, event.currentItem.item.id, { name: newName }).subscribe(
         (updatedItem) => {
            event.renameResultCallback('success');
            this.displayedItems.items[event.index] = this.OneDriveItemToItemDMS(updatedItem);
         },
         (error) => {
            event.renameResultCallback('error');
            this.loader.hide();
            console.error(error);

            const onedrive_error: PublicError = error?.error?.error;

            // Error from OneDrive API

            if (onedrive_error?.code === 'nameAlreadyExists') {
               this.translate.get('onedrive.error.nameAlreadyExists', { name: newName }).subscribe((s) => {
                  this.toast.error(s, 7000);
               });
            }
            // name ends with a dot
            else if (onedrive_error?.innerError?.code === 'nameContainsInvalidCharacters' && newName.endsWith('.')) {
               this.translate.get('alert.create_folder_error_dot').subscribe((s) => {
                  this.toast.error(s);
               });
            }
            // OneDrive Auth Token Expired
            else if (onedrive_error?.code === 'InvalidAuthenticationToken') {
               this.handleOneDriveTokenExpired();
            }
            // OneDrive error: The item is locked, eTag mismatch, lock token does not match, etc.
            else if (error.status === 423) {
               this.translate.get('onedrive.error.423').subscribe((s) => {
                  this.toast.error(s);
               });
            }
            // Other OneDrive error
            else if (onedrive_error) {
               this.translate
                  .get('onedrive.error.cannotRename', {
                     newName: event.newName,
                     errorMessage: onedrive_error.message,
                     errorCode: onedrive_error.innerError?.code ?? onedrive_error.code
                  })
                  .subscribe((s) => {
                     this.toast.error(s, 7000);
                  });
            }

            // Error from zDox: zDOX token is expired.
            else if (error.status === 401) {
               this.handlezDOXTimeout();
            } else {
               this.toast.error(error);
            }
         },
         () => {
            this.loader.hide();
         }
      );
   }

   protected downloadItem(event: RequestDownloadEvent<OneDriveItem>) {
      if (!event?.item?.item) return;

      const item = event.item.item;
      this.service.oneDriveGetDownloadUrl(this.access_token, item.id).subscribe((res) => {
         const download_url = res.message;
         this.saveFileFromUrl(download_url);
      });
   }

   protected async onClickDelete(event: RequestDeleteEvent<OneDriveItem>) {
      if (!event?.item?.item) return;

      const item = event.item.item;
      const dialogRef = this.dialog.open<ConfirmDeleteComponent, unknown, boolean>(ConfirmDeleteComponent, {
         disableClose: true,
         data: {
            item: item,
            message: await this.translate.get('onedrive.message.delete').toPromise()
         }
      });
      dialogRef.afterClosed().subscribe((isConfirmDelete) => {
         if (!isConfirmDelete) return;
         this.deleteItem(this.access_token, item);
      });
   }

   //=============================================================================
   // OTHER FUNCTIONS/METHODS
   //=============================================================================

   private saveFileFromUrl(url: string) {
      const link = document.createElement('a');
      link.href = url;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
   }

   /**
    * Send a PATCH request directly to Microsoft to update a DriveItem in OneDrive.
    *
    * Our backend has problem to handle PATCH request. Thus, we use the frontend to handle this.
    *
    * @param data Request body: should be the values for properties to be updated.
    * @param accessToken Bearer Token for Microsoft OneDrive API
    */
   private updateOneDriveItem(
      access_token: string,
      itemId: OneDriveItem['id'],
      data: OneDriveItem,
      eTag?: OneDriveItem['eTag'] | OneDriveItem['cTag']
   ): Observable<OneDriveItem> {
      const url = `https://graph.microsoft.com/v1.0/me/drive/items/${itemId}`;
      let headers = {
         Authorization: `Bearer ${access_token}`,
         'Content-Type': 'application/json'
      };
      if (eTag) {
         headers['If-Match'] = eTag;
      }

      return this.http.patch(url, data, { headers });
   }

   private deleteItem(access_token: string, item: OneDriveItem): void {
      this.loader.show();
      this.service.onedriveDeleteItem(access_token, item.id).subscribe(
         (res) => {
            this.translate.get('alert.confirm_delete').subscribe((s) => {
               this.toast.success(s);
            });
            this.refreshList('soft');
         },
         (error) => {
            this.loader.hide();
            console.error(error);

            const onedrive_error: PublicError = error?.error?.error;

            // Error from OneDrive API
            // OneDrive Auth Token Expired
            if (onedrive_error?.code === 'InvalidAuthenticationToken') {
               this.handleOneDriveTokenExpired();
            }
            // OneDrive error: The item is locked, eTag mismatch, lock token does not match, etc.
            else if (error.status === 423) {
               this.translate.get('onedrive.error.423').subscribe((s) => {
                  this.toast.error(s);
               });
            }
            // Other OneDrive error
            else if (onedrive_error) {
               this.translate
                  .get('onedrive.error.cannotDelete', {
                     errorMessage: onedrive_error.message,
                     errorCode: onedrive_error.innerError?.code ?? onedrive_error.code
                  })
                  .subscribe((s) => {
                     this.toast.error(s, 7000);
                  });
            }
            // Error from zDox: zDOX token is expired.
            else if (error.status === 401) {
               this.handlezDOXTimeout();
            } else {
               try {
                  this.toast.error(error.message);
               } catch (e) {
                  this.toast.error(error);
               }
            }
         },
         () => {
            this.loader.hide();
         }
      );
   }

   protected startOnedrive(type: 'document' | 'sheet' | 'presentation') {
      const onedrive = this.dialog.open(CreateOneDriveComponent, {
         disableClose: true,
         data: {
            from: 'onedrive',
            isFolder: false,
            parentId: this.folderItem ? this.folderItem.id : 'root'
         }
      });
      onedrive.componentInstance.doctype = type;
      onedrive.componentInstance.path = this.pathFolder;
      onedrive.afterClosed().subscribe((result$) => {
         result$.subscribe(() => {
            this.refreshList('soft');
         });
      });
   }

   newFolder() {
      const onedrive = this.dialog.open(CreateOneDriveComponent, {
         disableClose: true,
         data: {
            isFolder: true,
            parentId: this.folderItem ? this.folderItem.id : 'root'
         }
      });
      onedrive.afterClosed().subscribe((result$) => {
         result$.subscribe(() => {
            this.refreshList('soft');
         });
      });
   }

   protected uploadSelected(myFiles): void {
      let tempFiles = [];
      for (const file of myFiles) {
         file.uploadFolderId = this.pathFolder;
         tempFiles.push(file);
      }
      this.onedriveService.uploadFiles(
         localStorage.getItem('access_token'),
         tempFiles,
         this.uploadCallback,
         this.pathFolder
      );
   }

   uploadFolder(files) {
      this.onedriveService.uploadFolder(
         localStorage.getItem('access_token'),
         files,
         this.uploadCallback,
         this.pathFolder
      );
   }

   onDragOver(event: DragEvent) {
      const data = event.dataTransfer;
      let hasFiles = false;
      if (data.items) {
         for (let i = 0; i < data.items.length; i++) {
            if (data.items[i].kind === 'file') {
               hasFiles = true;
               break;
            }
         }
      } else if (data.files && data.files.length > 0) {
         hasFiles = data.files && data.files.length > 0;
      }
      if (hasFiles && !this.onedriveService.busy) {
         event.preventDefault();
         event.stopPropagation();
         event.dataTransfer.dropEffect = 'copy';
         this.renderer.addClass(this.onedriveList.nativeElement, 'drag-over');
      }
   }

   onDrop(e: DragEvent) {
      e.preventDefault();
      e.stopPropagation();
      this.renderer.removeClass(this.onedriveList.nativeElement, 'drag-over');

      this.onedriveService.process(this.access_token, e.dataTransfer, this.uploadCallback, this.pathFolder);
   }

   onDragLeave() {
      this.renderer.removeClass(this.onedriveList.nativeElement, 'drag-over');
   }

   /**
    * Handle Error 401: zDOX Timeout\
    * Redirect to login page and show a timeout message.
    */
   private handlezDOXTimeout() {
      this.translate.get('timeout').subscribe((s) => {
         this.toast.error(s);
      });
      this.router.navigate(['/login']);
   }

   /**
    * Handle OneDrive Token Expired
    *
    * Remove the expired token and reload page to let onedrive-auth-guard.service handle the login.
    */
   private handleOneDriveTokenExpired() {
      this.service.removeMicrosoftAuth();
      window.location.reload();
   }
}
